/**
 * Ticket → GitHub deployment target helpers (Game Studio + AI Studio).
 */

export const PROJECT_KIND_GAME = 'game';
export const PROJECT_KIND_APP = 'app';

/**
 * @param {'game'|'app'} kind
 * @param {string} projectId
 * @returns {string}
 */
export function buildTargetRepoSelectKey(kind, projectId) {
  const id = typeof projectId === 'string' ? projectId.trim() : '';
  if (!id) return '';
  if (kind === PROJECT_KIND_APP) return `app:${id}`;
  return `game:${id}`;
}

/**
 * @param {string} selectKey
 * @returns {{ kind: 'game'|'app', projectId: string }|null}
 */
export function parseTargetRepoSelectKey(selectKey) {
  const key = typeof selectKey === 'string' ? selectKey.trim() : '';
  if (!key) return null;
  if (key.startsWith('app:')) {
    const projectId = key.slice(4).trim();
    return projectId ? { kind: PROJECT_KIND_APP, projectId } : null;
  }
  if (key.startsWith('game:')) {
    const projectId = key.slice(5).trim();
    return projectId ? { kind: PROJECT_KIND_GAME, projectId } : null;
  }
  // Legacy bare game project id (pre-prefix keys)
  return { kind: PROJECT_KIND_GAME, projectId: key };
}

/**
 * @param {object|null|undefined} project
 * @param {'game'|'app'} kind
 * @returns {{
 *   owner: string,
 *   repoName: string,
 *   fullUrl: string,
 *   projectKind: 'game'|'app',
 *   gameProjectId?: string,
 *   appProjectId?: string,
 * }|null}
 */
export function buildTargetRepoFromProject(project, kind) {
  if (!project || typeof project !== 'object') return null;
  const owner = typeof project.githubOwner === 'string' ? project.githubOwner.trim() : '';
  const repoName =
    typeof project.githubRepoName === 'string' ? project.githubRepoName.trim() : '';
  if (!owner || !repoName) return null;
  const fullUrl =
    typeof project.githubRepoUrl === 'string' && project.githubRepoUrl.trim()
      ? project.githubRepoUrl.trim()
      : `https://github.com/${owner}/${repoName}`;
  const projectId = typeof project.id === 'string' ? project.id : '';
  const base = {
    owner,
    repoName,
    fullUrl,
    projectKind: kind === PROJECT_KIND_APP ? PROJECT_KIND_APP : PROJECT_KIND_GAME,
  };
  if (kind === PROJECT_KIND_APP) {
    return { ...base, appProjectId: projectId };
  }
  return { ...base, gameProjectId: projectId };
}

/** @deprecated Prefer buildTargetRepoFromProject(project, 'game') */
export function buildTargetRepoFromGameProject(project) {
  return buildTargetRepoFromProject(project, PROJECT_KIND_GAME);
}

/**
 * @param {object|null|undefined} targetRepo
 * @returns {boolean}
 */
export function isValidTargetRepo(targetRepo) {
  return Boolean(
    targetRepo &&
      typeof targetRepo === 'object' &&
      typeof targetRepo.owner === 'string' &&
      targetRepo.owner.trim() &&
      typeof targetRepo.repoName === 'string' &&
      targetRepo.repoName.trim(),
  );
}

/**
 * Dropdown key for a ticket's targetRepo ('' = main platform repo).
 * @param {object|null|undefined} targetRepo
 * @param {{ gameProjects?: Array<object>, appProjects?: Array<object> }|Array<object>} [lists]
 * @returns {string}
 */
export function resolveTargetRepoSelectKey(targetRepo, lists = []) {
  // Back-compat: second arg used to be gameProjects array
  const gameProjects = Array.isArray(lists)
    ? lists
    : Array.isArray(lists?.gameProjects)
      ? lists.gameProjects
      : [];
  const appProjects = Array.isArray(lists)
    ? []
    : Array.isArray(lists?.appProjects)
      ? lists.appProjects
      : [];

  if (!isValidTargetRepo(targetRepo)) return '';

  if (typeof targetRepo.appProjectId === 'string' && targetRepo.appProjectId.trim()) {
    return buildTargetRepoSelectKey(PROJECT_KIND_APP, targetRepo.appProjectId);
  }
  if (typeof targetRepo.gameProjectId === 'string' && targetRepo.gameProjectId.trim()) {
    return buildTargetRepoSelectKey(PROJECT_KIND_GAME, targetRepo.gameProjectId);
  }

  const owner = targetRepo.owner.trim().toLowerCase();
  const repoName = targetRepo.repoName.trim().toLowerCase();
  const matchApp = (appProjects || []).find(
    (p) =>
      String(p.githubOwner || '').trim().toLowerCase() === owner &&
      String(p.githubRepoName || '').trim().toLowerCase() === repoName,
  );
  if (matchApp?.id) {
    return buildTargetRepoSelectKey(PROJECT_KIND_APP, matchApp.id);
  }
  const matchGame = (gameProjects || []).find(
    (p) =>
      String(p.githubOwner || '').trim().toLowerCase() === owner &&
      String(p.githubRepoName || '').trim().toLowerCase() === repoName,
  );
  return matchGame?.id
    ? buildTargetRepoSelectKey(PROJECT_KIND_GAME, matchGame.id)
    : '';
}

/**
 * Firestore fields to persist for the selected dropdown key.
 * @param {string} selectKey
 * @param {{ gameProjects?: Array<object>, appProjects?: Array<object> }|Array<object>} lists
 * @returns {{ targetRepo: object|null, githubOwner: string|null, githubRepo: string|null }}
 */
export function buildTicketTargetRepoPayload(selectKey, lists = []) {
  const gameProjects = Array.isArray(lists)
    ? lists
    : Array.isArray(lists?.gameProjects)
      ? lists.gameProjects
      : [];
  const appProjects = Array.isArray(lists)
    ? []
    : Array.isArray(lists?.appProjects)
      ? lists.appProjects
      : [];

  const parsed = parseTargetRepoSelectKey(selectKey);
  if (!parsed) {
    return {
      targetRepo: null,
      githubOwner: null,
      githubRepo: null,
    };
  }

  const pool =
    parsed.kind === PROJECT_KIND_APP ? appProjects : gameProjects;
  const project = (pool || []).find((p) => p.id === parsed.projectId);
  const targetRepo = buildTargetRepoFromProject(project, parsed.kind);
  if (!targetRepo) {
    return {
      targetRepo: null,
      githubOwner: null,
      githubRepo: null,
    };
  }
  return {
    targetRepo,
    githubOwner: targetRepo.owner,
    githubRepo: targetRepo.repoName,
  };
}

/** Max secondary repos an agent may read on one ticket (primary is separate). */
export const MAX_LINKED_READ_REPOS = 3;

/**
 * @param {object|null|undefined} targetRepo
 * @returns {string}
 */
export function formatTargetRepoSlug(targetRepo) {
  if (!isValidTargetRepo(targetRepo)) return '';
  return `${targetRepo.owner.trim()}/${targetRepo.repoName.trim()}`;
}

/**
 * @param {string} owner
 * @param {string} repoName
 * @returns {string}
 */
function repoIdentityKey(owner, repoName) {
  return `${String(owner || '').trim().toLowerCase()}/${String(repoName || '')
    .trim()
    .toLowerCase()}`;
}

/**
 * Dropdown keys for a ticket's linkedReadRepos (excludes invalid entries).
 * @param {unknown} linkedReadRepos
 * @param {{ gameProjects?: Array<object>, appProjects?: Array<object> }|Array<object>} [lists]
 * @returns {string[]}
 */
export function resolveLinkedReadRepoSelectKeys(linkedReadRepos, lists = {}) {
  const rows = Array.isArray(linkedReadRepos) ? linkedReadRepos : [];
  const keys = [];
  const seen = new Set();
  for (const row of rows) {
    const key = resolveTargetRepoSelectKey(row, lists);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    keys.push(key);
    if (keys.length >= MAX_LINKED_READ_REPOS) break;
  }
  return keys;
}

/**
 * Firestore fields for additional READ-ONLY repos (never the write target).
 * @param {string[]} selectKeys
 * @param {{ gameProjects?: Array<object>, appProjects?: Array<object> }|Array<object>} lists
 * @param {{ primarySelectKey?: string }} [opts]
 * @returns {{ linkedReadRepos: object[] }}
 */
export function buildLinkedReadReposPayload(selectKeys, lists = {}, opts = {}) {
  const primaryKey =
    typeof opts.primarySelectKey === 'string' ? opts.primarySelectKey.trim() : '';
  const rawKeys = Array.isArray(selectKeys) ? selectKeys : [];
  const linkedReadRepos = [];
  const seenIdentity = new Set();

  let primaryIdentity = '';
  if (primaryKey) {
    const primaryPayload = buildTicketTargetRepoPayload(primaryKey, lists);
    if (isValidTargetRepo(primaryPayload.targetRepo)) {
      primaryIdentity = repoIdentityKey(
        primaryPayload.targetRepo.owner,
        primaryPayload.targetRepo.repoName,
      );
    }
  }

  for (const raw of rawKeys) {
    const key = typeof raw === 'string' ? raw.trim() : '';
    if (!key || key === primaryKey) continue;
    const payload = buildTicketTargetRepoPayload(key, lists);
    if (!isValidTargetRepo(payload.targetRepo)) continue;
    const identity = repoIdentityKey(
      payload.targetRepo.owner,
      payload.targetRepo.repoName,
    );
    if (!identity || identity === primaryIdentity || seenIdentity.has(identity)) {
      continue;
    }
    seenIdentity.add(identity);
    linkedReadRepos.push(payload.targetRepo);
    if (linkedReadRepos.length >= MAX_LINKED_READ_REPOS) break;
  }

  return { linkedReadRepos };
}
